<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="data-item-modal">
      <div class="modal-header">
        <h2 class="text-lg font-medium text-slate-900">
          {{ isEditing ? $t('BUSINESS_DATA.ITEMS.EDIT') : $t('BUSINESS_DATA.ITEMS.ADD_NEW') }}
        </h2>
      </div>
      
      <form @submit.prevent="onSubmit" class="modal-form">
        <!-- Title field -->
        <div class="form-row">
          <label class="form-label">
            {{ $t('BUSINESS_DATA.ITEMS.TITLE_FIELD') }} *
          </label>
          <input
            v-model="formData.title"
            :placeholder="$t('BUSINESS_DATA.ITEMS.TITLE_PLACEHOLDER')"
            class="form-input"
            required
            maxlength="100"
          />
          <div class="form-help text-right">
            <span :class="formData.title.length > 80 ? 'text-red-500' : 'text-gray-500'">
              {{ formData.title.length }}/100
            </span>
          </div>
        </div>
        
        <!-- Description field -->
        <div class="form-row">
          <label class="form-label">
            {{ $t('BUSINESS_DATA.ITEMS.DESCRIPTION') }} *
          </label>
          <textarea
            v-model="formData.description"
            :placeholder="$t('BUSINESS_DATA.ITEMS.DESCRIPTION_PLACEHOLDER')"
            class="form-textarea"
            rows="4"
            required
            maxlength="2000"
          />
          <div class="form-help text-right">
            <span :class="formData.description.length > 1800 ? 'text-red-500' : 'text-gray-500'">
              {{ formData.description.length }}/2000
            </span>
          </div>
        </div>
        
        <!-- Category field -->
        <div class="form-row">
          <label class="form-label">
            {{ $t('BUSINESS_DATA.ITEMS.CATEGORY') }}
          </label>
          <CategorySelector
            v-model="formData.business_category_id"
            :categories="businessCategories"
            category-type="data"
            :placeholder="$t('BUSINESS_DATA.ITEMS.CATEGORY_PLACEHOLDER')"
            @category-created="onCategoryCreated"
          />
          <div class="form-help">
            {{ $t('BUSINESS_DATA.ITEMS.CATEGORY_HELP') }}
          </div>
        </div>
        
        
        <!-- Active toggle -->
        <div class="form-row">
          <div class="flex items-center">
            <woot-switch
              :model-value="formData.is_active"
              @input="formData.is_active = $event"
            />
            <span class="ml-3 text-sm font-medium text-gray-700">
              {{ $t('BUSINESS_DATA.ITEMS.ACTIVE') }}
            </span>
          </div>
          <div class="form-help">
            {{ $t('BUSINESS_DATA.ITEMS.ACTIVE_HELP') }}
          </div>
        </div>
        
        <!-- Actions -->
        <div class="modal-footer">
          <woot-button
            type="button"
            variant="clear"
            @click="onClose"
          >
            {{ $t('COMMON.CANCEL') }}
          </woot-button>
          
          <woot-button
            type="submit"
            color-scheme="primary"
            :loading="isSaving"
            :disabled="!isFormValid"
          >
            {{ isEditing ? $t('COMMON.UPDATE') : $t('COMMON.CREATE') }}
          </woot-button>
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<script>
import { useAlert } from 'dashboard/composables';
import CategorySelector from './CategorySelector.vue';

export default {
  name: 'DataItemModal',
  components: { CategorySelector },
  
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    dataItem: {
      type: Object,
      default: null,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    businessCategories: {
      type: Array,
      default: () => [],
    },
  },
  
  data() {
    return {
      isSaving: false,
      formData: {
        title: '',
        description: '',
        category: '',
        business_category_id: null,
        is_active: true,
      },
    };
  },
  
  computed: {
    isEditing() {
      return !!this.dataItem;
    },
    
    isFormValid() {
      return this.formData.title.trim() && 
             this.formData.description.trim() &&
             this.formData.title.length <= 5000 &&
             this.formData.description.length <= 5000;
    },
    
    existingCategories() {
      return this.categories.filter(cat => cat && cat.trim());
    },
    
    currentAccountId() {
      return this.$store.getters.getCurrentAccountId;
    },
  },
  
  watch: {
    dataItem: {
      immediate: true,
              handler(newItem) {
          if (newItem) {
            this.formData = {
              title: newItem.title || '',
              description: newItem.description || '',
              category: newItem.category || '',
              business_category_id: newItem.business_category_id || null,
              is_active: newItem.is_active !== undefined ? newItem.is_active : true,
            };
          } else {
            this.resetForm();
          }
        },
    },
    
    show(newShow) {
      if (!newShow) {
        this.resetForm();
      }
    },
    
    businessCategories: {
      immediate: true,
      handler(newCategories) {
        console.log('DataItemModal received business categories:', newCategories);
      },
    },
  },
  
  methods: {
    resetForm() {
      this.formData = {
        title: '',
        description: '',
        category: '',
        business_category_id: null,
        is_active: true,
      };
    },
    
    onCategoryCreated(newCategory) {
      this.$emit('category-created', newCategory);
    },
    
    async onSubmit() {
      if (!this.isFormValid) {
        return;
      }
      
      this.isSaving = true;
      try {
        const payload = {
          title: (this.formData.title || '').trim(),
          description: (this.formData.description || '').trim(),
          category: (this.formData.category || '').trim() || null,
          business_category_id: this.formData.business_category_id,
          is_active: this.formData.is_active,
        };
        
        if (this.isEditing) {
          await window.axios.put(
            `/api/v1/accounts/${this.currentAccountId}/business_data_items/${this.dataItem.id}`,
            payload
          );
        } else {
          await window.axios.post(
            `/api/v1/accounts/${this.currentAccountId}/business_data_items`,
            payload
          );
        }
        
        this.$emit('saved');
      } catch (error) {
        console.error('Error saving data item:', error);
        console.error('Error response:', error?.response?.data);
        
        let errorMessage = this.$t('BUSINESS_DATA.ERROR.SAVE_FAILED');
        
        if (error?.response?.data?.errors) {
          errorMessage = error.response.data.errors.join(', ');
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        useAlert(errorMessage, 'error');
      } finally {
        this.isSaving = false;
      }
    },
    
    onClose() {
      this.$emit('close');
    },
  },
  
  emits: ['close', 'saved', 'category-created'],
};
</script>

<style lang="scss" scoped>
.data-item-modal {
  width: 100%;
  max-width: 600px;
}

.modal-header {
  margin-bottom: var(--space-large);
  
  h2 {
    margin: 0;
    color: var(--color-heading);
    font-size: var(--font-size-large);
  }
}

.modal-form {
  .form-row {
    margin-bottom: var(--space-normal);
    
    .form-label {
      display: block;
      margin-bottom: var(--space-smaller);
      font-weight: var(--font-weight-medium);
      color: var(--color-heading);
    }
    
    .form-input,
    .form-textarea {
      width: 100%;
      padding: var(--space-small);
      border: 1px solid var(--color-border);
      border-radius: var(--border-radius-normal);
      font-size: var(--font-size-default);
      
      &:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: 0 0 0 1px var(--color-primary);
      }
    }
    
    .form-textarea {
      resize: vertical;
      min-height: 80px;
    }
    
    .form-help {
      margin-top: var(--space-smaller);
      font-size: var(--font-size-small);
      color: var(--color-text-light);
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-small);
  margin-top: var(--space-large);
  padding-top: var(--space-normal);
  border-top: 1px solid var(--color-border-light);
}

@media (max-width: 768px) {
  .data-item-modal {
    max-width: 100%;
  }
}
</style>
